var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect } from "react";
import FocusTrap from "focus-trap-react";
import { AnimatePresence, motion } from "framer-motion";
import { useTheme } from "styled-components";
import { Text } from "../Text";
import { ModalHeader } from "./ModalHeader";
import { CloseButton } from "./internal/ModalCloseButton";
import { ModalOverlay } from "./internal/ModalOverlay";
import { ModalContainer } from "./internal/ModalContainer";
/**
 * An animated modal container that slides down/up on mounting/unmounting
 * @param {React.ReactNode} children the modal's content
 * @param {boolean} fullscreen if true, sets flex to 1 and min-height to 100%
 * @returns a react component
 */
const AnimatedModalContainer = (_a) => {
    var { children, variant } = _a, props = __rest(_a, ["children", "variant"]);
    const variants = {
        hide: {
            marginTop: [0, -50],
            opacity: [1, 0],
            transition: {
                duration: 1,
            },
        },
        show: {
            marginTop: [-50, 0],
            opacity: [0, 1],
            transition: {
                delay: 0.1,
                duration: 1,
            },
        },
    };
    return (React.createElement(motion.div, { variants: variants, initial: "hide", animate: "show", exit: "hide" },
        React.createElement(ModalContainer, Object.assign({ variant: variant, "data-testid": "modal-container" }, props), children)));
};
/*
 * A container component to capture when escape key is pressed inside the modal
 */
const EscapeListener = ({ children, onClose, open }) => {
    const onKeyDown = (event) => {
        if ((event.key === "Escape" || event.key === "Esc") && open) {
            onClose(event);
        }
    };
    return (
    // Key down handler captures events from children
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    React.createElement("div", { onKeyDown: onKeyDown }, children));
};
/*
 * A Modal is an overlay view that displays over the entire page.
 *
 * @example
 * <Modal title="This is the modal title.">
 *   <ModalBody>
 *     This is the modal body.
 *   </ModalBody>
 *   <ModalFooter>
 *     <Button onClose={onClose} size="medium">Cancel</Button>
 *   </ModalFooter>
 * </Modal>
 */
export const Modal = (_a) => {
    var { children, closeButtonText = "Close", onClose, open, title, variant = "normal", modalOverlayHeight, zIndexOverride } = _a, props = __rest(_a, ["children", "closeButtonText", "onClose", "open", "title", "variant", "modalOverlayHeight", "zIndexOverride"]);
    useEffect(() => {
        const { overflow: oldOverflow, height: oldHeight } = document.body.style;
        if (open) {
            document.body.style.overflow = "hidden";
            document.body.style.height = "100%";
        }
        return () => {
            if (open) {
                document.body.style.overflow = oldOverflow;
                document.body.style.height = oldHeight;
            }
        };
    }, [open]);
    const { colors: { modalColors }, } = useTheme();
    return (React.createElement(AnimatePresence, null, open && (React.createElement(ModalOverlay, { key: "modalOverlayKey", onClose: onClose, modalOverlayHeight: modalOverlayHeight, zIndexOverride: zIndexOverride },
        React.createElement(EscapeListener, { onClose: onClose, open: open },
            React.createElement(AnimatedModalContainer, Object.assign({ key: "animatedModalContainer", variant: variant, "data-testid": "modal-container" }, props),
                React.createElement(FocusTrap, { focusTrapOptions: {
                        allowOutsideClick: true,
                        escapeDeactivates: false,
                        tabbableOptions: {
                            displayCheck: "legacy-full",
                        },
                    } },
                    React.createElement("div", { role: "dialog", "aria-modal": "true", "aria-label": title, "aria-describedby": "modal-title" },
                        React.createElement(ModalHeader, null,
                            React.createElement(Text, { fontSize: 4, fontWeight: 100, color: modalColors.modalHeaderText, id: "modal-title" }, title),
                            React.createElement(CloseButton, { ariaLabel: closeButtonText, onClose: onClose })),
                        children))))))));
};
